// GSM similar-items engine for the storefront (related grid + cart drawer
// + cart-page suggestions).
// Port of the post-purchase ranking in gsm_discount_fn/post-purchase-signer/
// lib/similar.js: same player (title match) > same team (tag) > same
// league + item type > same sport + item type, with the same hard
// exclusions (national signers / pre-orders / signings / service fees) and
// dedupe by handle AND by title+price (the catalog has duplicate listings).
// similar() ranks against one seed product (drawer, related grid);
// similarMulti() merges keys across the whole cart (cart page).
//
// Storefront differences vs the post-purchase version:
//  - Queries go through plain-text storefront search (tag: filters are
//    Admin-API-only), served as JSON by templates/search.gsm-similar-json.
//  - FULL PRICE ONLY. Pre-checkout surfaces never show the post-purchase
//    discount bands.
//  - Backfill pool is the Under $50 collection (best-selling default sort).
window.GSMSimilar = (function () {
  var VIEW = 'gsm-similar-json';
  var BACKFILL_COLLECTION = 'under-50';

  // ---- canonical vocabularies (lowercase), from the 2026-07-30 retag ----
  var LEAGUES = { nba: 1, wnba: 1, mlb: 1, nfl: 1, nhl: 1, ncaa: 1 };
  var SPORTS = {
    basketball: 1, baseball: 1, football: 1, hockey: 1, wrestling: 1,
    boxing: 1, mma: 1, golf: 1, tennis: 1, soccer: 1, racing: 1,
    olympic: 1, music: 1, celebrity: 1, entertainment: 1, politics: 1,
    business: 1
  };
  var ITEM_FAMILIES = [
    ['card', ['card', 'trading card']],
    ['photo', ['photo']],
    ['jersey', ['jersey']],
    ['ball', ['ball', 'autographed baseball', 'autographed football', 'autographed basketball', 'romlb']],
    ['helmet', ['helmet', 'mini helmet', 'mini-helmet']],
    ['puck', ['puck', 'hockey puck']],
    ['bat', ['bat', 'baseball bat']],
    ['glove', ['glove']],
    ['funko', ['funko', 'funko pop']],
    ['bobblehead', ['bobblehead']],
    ['album', ['album']],
    ['magazine', ['magazine']],
    ['book', ['book']],
    ['pennant', ['pennant']],
    ['ticket', ['ticket']],
    ['shoe', ['shoes']],
    ['shorts', ['shorts']],
    ['trunks', ['trunks']],
    ['guitar', ['guitar']]
  ];
  var ITEM_SEARCH_TAG = {
    card: 'trading card', photo: 'photo', jersey: 'jersey', ball: 'ball',
    helmet: 'helmet', puck: 'puck', bat: 'bat', glove: 'glove',
    funko: 'funko', bobblehead: 'bobblehead', album: 'album',
    magazine: 'magazine', book: 'book', pennant: 'pennant',
    ticket: 'ticket', shoe: 'shoes', shorts: 'shorts', trunks: 'trunks',
    guitar: 'guitar'
  };

  // Full-name team tags only (multi-word forms are what we search with —
  // "warriors" alone would text-match half the catalog's descriptions).
  var TEAM_TAGS = [
    'golden state warriors', 'los angeles lakers', 'la lakers', 'boston celtics', 'chicago bulls', 'new york knicks', 'brooklyn nets', 'new jersey nets', 'philadelphia 76ers', 'toronto raptors', 'miami heat', 'orlando magic', 'atlanta hawks', 'charlotte hornets', 'washington wizards', 'cleveland cavaliers', 'detroit pistons', 'indiana pacers', 'milwaukee bucks', 'minnesota timberwolves', 'oklahoma city thunder', 'portland trail blazers', 'utah jazz', 'denver nuggets', 'phoenix suns', 'los angeles clippers', 'la clippers', 'dallas mavericks', 'san antonio spurs', 'houston rockets', 'memphis grizzlies', 'new orleans pelicans', 'sacramento kings', 'seattle supersonics',
    'new york yankees', 'red sox', 'boston red sox', 'los angeles dodgers', 'la dodgers', 'san francisco giants', 'sf giants', 'oakland athletics', "oakland a's", 'los angeles angels', 'anaheim angels', 'houston astros', 'seattle mariners', 'texas rangers', 'minnesota twins', 'chicago white sox', 'cleveland guardians', 'cleveland indians', 'detroit tigers', 'kansas city royals', 'baltimore orioles', 'toronto blue jays', 'tampa bay rays', 'atlanta braves', 'new york mets', 'philadelphia phillies', 'washington nationals', 'miami marlins', 'chicago cubs', 'st. louis cardinals', 'st louis cardinals', 'milwaukee brewers', 'pittsburgh pirates', 'cincinnati reds', 'colorado rockies', 'san diego padres', 'arizona diamondbacks',
    'dallas cowboys', 'green bay packers', 'new england patriots', 'pittsburgh steelers', 'san francisco 49ers', 'las vegas raiders', 'oakland raiders', 'kansas city chiefs', 'denver broncos', 'seattle seahawks', 'philadelphia eagles', 'new york giants', 'ny giants', 'miami dolphins', 'buffalo bills', 'new york jets', 'ny jets', 'cleveland browns', 'cincinnati bengals', 'baltimore ravens', 'tennessee titans', 'indianapolis colts', 'jacksonville jaguars', 'houston texans', 'los angeles chargers', 'san diego chargers', 'los angeles rams', 'st. louis rams', 'arizona cardinals', 'atlanta falcons', 'carolina panthers', 'new orleans saints', 'tampa bay buccaneers', 'minnesota vikings', 'detroit lions', 'chicago bears', 'washington commanders', 'washington redskins', 'washington football team',
    'san jose sharks', 'boston bruins', 'toronto maple leafs', 'montreal canadiens', 'new york rangers', 'ny rangers', 'new york islanders', 'philadelphia flyers', 'pittsburgh penguins', 'washington capitals', 'carolina hurricanes', 'florida panthers', 'tampa bay lightning', 'detroit red wings', 'chicago blackhawks', 'colorado avalanche', 'st. louis blues', 'st louis blues', 'minnesota wild', 'nashville predators', 'winnipeg jets', 'calgary flames', 'edmonton oilers', 'vancouver canucks', 'seattle kraken', 'vegas golden knights', 'anaheim ducks', 'arizona coyotes', 'buffalo sabres', 'ottawa senators', 'new jersey devils', 'los angeles kings', 'la kings', 'dallas stars'
  ];
  var TEAM_SET = {};
  TEAM_TAGS.forEach(function (t) { TEAM_SET[t] = 1; });

  // Hard exclusions — mirror post-purchase bestsellers.js.
  var TITLE_EXCLUDE = [
    'national 2026', 'national 2027', 'pre-order', 'preorder', 'pre order',
    'mail-in', 'mail in', 'signing', 'authentication submission', 'submission'
  ];
  var TAG_EXCLUDE = {};
  ['national 2026', 'national 2027', 'mail-in pre-order', 'mail-in', 'mail in',
    'pre-order', 'preorder', 'signing', 'signer'].forEach(function (t) { TAG_EXCLUDE[t] = 1; });

  var NAME_STOP = {
    signed: 1, autographed: 1, auto: 1, unsigned: 1, encapsulated: 1,
    custom: 1, framed: 1, official: 1, psa: 1, 'psa/dna': 1, bas: 1,
    jsa: 1, beckett: 1, and: 1
  };

  // "Stephen Curry Golden State Warriors Autographed ..." -> "Stephen Curry".
  function playerFromTitle(title) {
    var t = String(title || '').trim();
    if (/^(19|20)\d{2}\b/.test(t)) return null; // set titles ("2024 Topps ...")
    t = t.replace(/^[#\d][\w./-]*\s+/, '');
    var out = [];
    var parts = t.split(/\s+/);
    for (var i = 0; i < parts.length; i++) {
      var clean = parts[i].replace(/[^A-Za-z0-9'.\-]/g, '');
      if (!clean || /^\d/.test(clean)) break;
      if (NAME_STOP[clean.toLowerCase()]) break;
      out.push(clean);
      if (out.length >= 2) break;
    }
    if (!out.length) return null;
    if (out.length === 1 && out[0].length < 3) return null;
    return out.join(' ');
  }

  function lowTagSet(tags) {
    var s = {};
    (tags || []).forEach(function (t) { s[String(t).toLowerCase()] = 1; });
    return s;
  }

  function familyOf(low) {
    for (var i = 0; i < ITEM_FAMILIES.length; i++) {
      var fam = ITEM_FAMILIES[i];
      for (var j = 0; j < fam[1].length; j++) {
        if (low[fam[1][j]]) return fam[0];
      }
    }
    return null;
  }

  // What we know about the seed product -> match keys.
  function keysFor(seed) {
    var low = lowTagSet(seed.tags);
    var keys = { player: playerFromTitle(seed.title), teams: [], leagues: [], sports: [], item: familyOf(low) };
    for (var t in low) {
      if (TEAM_SET[t] && t.indexOf(' ') !== -1) keys.teams.push(t);
      if (LEAGUES[t]) keys.leagues.push(t);
      if (SPORTS[t]) keys.sports.push(t);
    }
    return keys;
  }

  function isExcluded(p) {
    var t = String(p.title || '').toLowerCase();
    for (var i = 0; i < TITLE_EXCLUDE.length; i++) {
      if (t.indexOf(TITLE_EXCLUDE[i]) !== -1) return true;
    }
    var low = lowTagSet(p.tags);
    for (var tag in low) {
      if (TAG_EXCLUDE[tag]) return true;
    }
    return false;
  }

  function score(p, keys) {
    var title = String(p.title || '').toLowerCase();
    var low = lowTagSet(p.tags);
    var s = 0;
    if (keys.player) {
      var all = keys.player.toLowerCase().split(/\s+/).every(function (tok) {
        return title.indexOf(tok) !== -1;
      });
      if (all) s += 8;
    }
    if (keys.teams.some(function (t) { return low[t]; })) s += 4;
    if (keys.leagues.some(function (l) { return low[l]; })) s += 2;
    if (keys.sports.some(function (sp) { return low[sp]; })) s += 1;
    if (keys.item && familyOf(low) === keys.item) s += 2;
    return s;
  }

  function jfetch(url) {
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (d) { return (d && d.products) || []; })
      .catch(function () { return []; });
  }

  function searchQ(q) {
    return jfetch('/search?view=' + VIEW + '&type=product&q=' + encodeURIComponent(q));
  }

  // Backfill pool fetch. Deliberately NO Accept: application/json header:
  // on /collections/<handle> URLs that header makes Shopify serve the
  // native collection JSON API ({"collection":{...}}, no products) and
  // ignore ?view= entirely, which silently killed the backfill.
  function collectionPool() {
    return fetch('/collections/' + BACKFILL_COLLECTION + '?view=' + VIEW)
      .then(function (r) { return r.json(); })
      .then(function (d) { return (d && d.products) || []; })
      .catch(function () { return []; });
  }

  // Similarity-ranked, exclusion-filtered, deduped items for a seed
  // product. opts: { count, excludeHandles: [] }. Resolves with an array of
  // {handle,title,url,image,price,compare_at,available,variant_id,sim}.
  function similar(seed, opts) {
    opts = opts || {};
    var count = opts.count || 6;
    var exclude = {};
    (opts.excludeHandles || []).forEach(function (h) { exclude[h] = 1; });
    if (seed.handle) exclude[seed.handle] = 1;

    var keys = keysFor(seed);
    var queries = [];
    if (keys.player) queries.push(keys.player);
    if (keys.teams.length) queries.push(keys.teams[0]);
    var itemTag = keys.item ? ITEM_SEARCH_TAG[keys.item] : null;
    if (keys.leagues.length) {
      queries.push(itemTag ? keys.leagues[0] + ' ' + itemTag : keys.leagues[0]);
    } else if (keys.sports.length) {
      queries.push(itemTag ? keys.sports[0] + ' ' + itemTag : keys.sports[0]);
    }
    queries = queries.slice(0, 3);

    var fetches = queries.map(searchQ);
    return Promise.all(fetches).then(function (results) {
      var byHandle = {};
      var seenListing = {};
      var out = [];

      function consider(p, simFlag) {
        if (!p || !p.available || !p.variant_id) return;
        if (exclude[p.handle] || byHandle[p.handle]) return;
        if (!(p.price > 0)) return;
        if (isExcluded(p)) return;
        var listingKey = String(p.title).trim().toLowerCase() + '|' + p.price;
        if (seenListing[listingKey]) return;
        byHandle[p.handle] = 1;
        seenListing[listingKey] = 1;
        p.sim = simFlag;
        out.push(p);
      }

      var candidates = [];
      results.forEach(function (list) {
        list.forEach(function (p) {
          p.__score = score(p, keys);
          if (p.__score > 0) candidates.push(p);
        });
      });
      candidates.sort(function (a, b) { return b.__score - a.__score; });
      candidates.forEach(function (p) {
        if (out.length < count) consider(p, true);
      });

      if (out.length >= count) return out.slice(0, count);

      // Backfill from the Under $50 pool (best-selling default sort).
      return collectionPool().then(function (pool) {
        pool.forEach(function (p) {
          if (out.length < count) consider(p, false);
        });
        return out.slice(0, count);
      });
    });
  }

  // ---- combined-cart variant (cart page) -------------------------------
  // Port of the post-purchase extractKeys: unique players / teams /
  // leagues / sports / item families merged across up to 5 seed products,
  // in cart order.
  function mergeKeys(seeds) {
    var keys = { players: [], teams: [], leagues: [], sports: [], items: [] };
    var seen = {};
    function push(list, val, ns) {
      if (!val) return;
      var k = ns + '|' + String(val).toLowerCase();
      if (seen[k]) return;
      seen[k] = 1;
      list.push(val);
    }
    (seeds || []).slice(0, 5).forEach(function (seed) {
      var one = keysFor(seed);
      push(keys.players, one.player, 'p');
      one.teams.forEach(function (t) { push(keys.teams, t, 't'); });
      one.leagues.forEach(function (l) { push(keys.leagues, l, 'l'); });
      one.sports.forEach(function (s) { push(keys.sports, s, 's'); });
      push(keys.items, one.item, 'i');
    });
    return keys;
  }

  // Similarity-ranked items for the combined cart. Candidates come from
  // merged-key searches (up to 5 player + 2 team + 2 league|sport-combo),
  // but each candidate is attributed to the CART LINE it scores highest
  // against and the grid round-robins across lines in cart order. Absolute
  // score alone can't be the rank: a fully-tagged Warriors line scores 17
  // while e.g. a music line tops out around 11, so one product would fill
  // the whole grid. Same exclusions, dedupe and Under-$50 backfill as
  // similar().
  function similarMulti(seeds, opts) {
    opts = opts || {};
    var count = opts.count || 8;
    var exclude = {};
    (opts.excludeHandles || []).forEach(function (h) { exclude[h] = 1; });
    (seeds || []).forEach(function (s) { if (s.handle) exclude[s.handle] = 1; });

    var seedList = (seeds || []).slice(0, 5);
    var seedKeys = seedList.map(keysFor);

    var keys = mergeKeys(seeds);
    var queries = [];
    keys.players.slice(0, 5).forEach(function (p) { queries.push(p); });
    keys.teams.slice(0, 2).forEach(function (t) { queries.push(t); });
    var itemTag = keys.items.length ? ITEM_SEARCH_TAG[keys.items[0]] : null;
    if (keys.leagues.length) {
      keys.leagues.slice(0, 2).forEach(function (l) {
        queries.push(itemTag ? l + ' ' + itemTag : l);
      });
    } else if (keys.sports.length) {
      queries.push(itemTag ? keys.sports[0] + ' ' + itemTag : keys.sports[0]);
    }
    if (!queries.length) queries = [];

    var fetches = queries.map(searchQ);
    return Promise.all(fetches).then(function (results) {
      var byHandle = {};
      var seenListing = {};
      var out = [];

      function consider(p, simFlag) {
        if (!p || !p.available || !p.variant_id) return;
        if (exclude[p.handle] || byHandle[p.handle]) return;
        if (!(p.price > 0)) return;
        if (isExcluded(p)) return;
        var listingKey = String(p.title).trim().toLowerCase() + '|' + p.price;
        if (seenListing[listingKey]) return;
        byHandle[p.handle] = 1;
        seenListing[listingKey] = 1;
        p.sim = simFlag;
        out.push(p);
      }

      // Attribute every candidate to the cart line it best matches, using
      // the same single-seed score() the drawer uses.
      var candidates = [];
      results.forEach(function (list) {
        list.forEach(function (p) {
          var best = 0;
          var bestSeed = 0;
          for (var si = 0; si < seedKeys.length; si++) {
            var s = score(p, seedKeys[si]);
            if (s > best) { best = s; bestSeed = si; }
          }
          if (best > 0) {
            p.__score = best;
            p.__seed = bestSeed;
            candidates.push(p);
          }
        });
      });
      candidates.sort(function (a, b) { return b.__score - a.__score; });

      // One bucket per cart line (best matches first), then round-robin
      // the buckets in cart order so every line in the cart is
      // represented in the grid.
      var buckets = seedKeys.map(function () { return []; });
      candidates.forEach(function (p) { buckets[p.__seed].push(p); });
      var took = true;
      while (took && out.length < count) {
        took = false;
        for (var b = 0; b < buckets.length && out.length < count; b++) {
          while (buckets[b].length) {
            var before = out.length;
            consider(buckets[b].shift(), true);
            if (out.length > before) { took = true; break; }
          }
        }
      }

      if (out.length >= count) return out.slice(0, count);

      return collectionPool().then(function (pool) {
        pool.forEach(function (p) {
          if (out.length < count) consider(p, false);
        });
        return out.slice(0, count);
      });
    });
  }

  return { similar: similar, similarMulti: similarMulti, keysFor: keysFor, playerFromTitle: playerFromTitle };
})();
